import { memo } from "react";
import { format } from "date-fns";
import { CellContext } from "@tanstack/react-table";
import { InvoiceStatus } from "@common/billing";
import { Invoice } from "@common/types/billing";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { currencyFormatter } from "@common/helpers/currency";
import { fromIntToMonetary } from "@common/helpers/monetary";
import {
    OrganizationBillingInvoicesColumn,
    OrganizationBillingInvoicesView,
} from "@common/types/views/organizationBillingInvoicesView";
import { ViewDensity, ViewType } from "@common/types/views/views";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { Button } from "@design-system/Button/Button";
import { DownloadIcon } from "@icons";
import { ViewConfig } from "@lib/views";

export interface BillingInvoicesViewRow {
    id: string;
    date: Date;
    status: string;
    amount: string;
    url: string | null;
}

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
    [InvoiceStatus.DRAFT]: "Draft",
    [InvoiceStatus.OPEN]: "Open",
    [InvoiceStatus.PAID]: "Paid",
    [InvoiceStatus.VOID]: "Voided",
    [InvoiceStatus.UNCOLLECTIBLE]: "Uncollectible",
};

export const buildBillingInvoicesRows = (
    invoices: Invoice[],
): BillingInvoicesViewRow[] =>
    invoices.map((invoice, index) => ({
        id: `${invoice.date.toISOString()}-${invoice.totalInCents}-${invoice.status}-${index}`,
        date: invoice.date,
        status: INVOICE_STATUS_LABEL[invoice.status],
        amount: currencyFormatter.format(
            fromIntToMonetary(invoice.totalInCents),
        ),
        url: invoice.url,
    }));

const DateCell = memo(function DateCell({
    row,
}: CellContext<ViewTableFeatures, BillingInvoicesViewRow, unknown>) {
    return (
        <ViewTableCell>
            {format(row.original.date, fullDateFullYearFormat)}
        </ViewTableCell>
    );
});

const StatusCell = memo(function StatusCell({
    row,
}: CellContext<ViewTableFeatures, BillingInvoicesViewRow, unknown>) {
    return <ViewTableCell>{row.original.status}</ViewTableCell>;
});

const AmountCell = memo(function AmountCell({
    row,
}: CellContext<ViewTableFeatures, BillingInvoicesViewRow, unknown>) {
    return <ViewTableCell>{row.original.amount}</ViewTableCell>;
});

const DownloadCell = memo(function DownloadCell({
    row,
}: CellContext<ViewTableFeatures, BillingInvoicesViewRow, unknown>) {
    const url = row.original.url;

    if (!url) {
        return <ViewTableCell />;
    }

    return (
        <ViewTableCell>
            <Button
                variant="primary"
                size="md"
                iconOnly
                aria-label="Download invoice"
                onClick={() => {
                    window.open(url, "_blank", "noopener,noreferrer");
                }}
            >
                <DownloadIcon />
            </Button>
        </ViewTableCell>
    );
});

export const billingInvoicesViewConfig: ViewConfig<
    BillingInvoicesViewRow,
    OrganizationBillingInvoicesColumn,
    never
> = {
    columns: {
        [OrganizationBillingInvoicesColumn.DATE]: {
            accessorKey: "date",
            name: "Date",
            header: "Date",
            immutable: true,
            cell: DateCell,
            minSize: 140,
            maxSize: 220,
        },
        [OrganizationBillingInvoicesColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "Status",
            cell: StatusCell,
            minSize: 120,
            maxSize: 180,
        },
        [OrganizationBillingInvoicesColumn.AMOUNT]: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            align: "right",
            cell: AmountCell,
            minSize: 100,
            maxSize: 160,
        },
        [OrganizationBillingInvoicesColumn.DOWNLOAD]: {
            accessorKey: "url",
            name: "Download",
            header: "",
            align: "right",
            cell: DownloadCell,
            minSize: 64,
            maxSize: 80,
        },
    },
    density: ViewDensity.SM,
};

export const createBillingInvoicesView =
    (): OrganizationBillingInvoicesView => ({
        type: ViewType.ORGANIZATION_BILLING_INVOICES,
        columns: [
            { id: OrganizationBillingInvoicesColumn.DATE },
            { id: OrganizationBillingInvoicesColumn.STATUS },
            { id: OrganizationBillingInvoicesColumn.AMOUNT },
            {
                id: OrganizationBillingInvoicesColumn.DOWNLOAD,
                options: { pin: "right" },
            },
        ],
        filters: {},
        options: {
            selection: false,
            bulkActions: false,
            density: ViewDensity.SM,
        },
    });
